/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  QuestionItemForm,
  QuestionOptionForm,
  QuestionSubmitItem
} from "@/serve/question/question";

export interface QuestionRule {
  sourceType?: string;
  sourceField?: string;
  itemId?: number | null;
  optionId?: number | null;
  conditions?: string;
  compareValue?: string;
  targetItemId?: number | null;
  action?: string;
}

export interface OptionAnswer {
  optionId: number;
  optionInput?: string;
}

export interface ItemAnswer {
  answerText: string;
  options: OptionAnswer[];
  optionInputs: { [key: number]: string };
}

export interface AnswerMap {
  [key: number]: ItemAnswer;
}

export interface ExternalContext {
  checkerGender?: string;
  checkerAge?: number | null;
}

export function createAnswerMap(items: QuestionItemForm[], current: AnswerMap = {}): AnswerMap {
  const next: AnswerMap = {};
  items.forEach(item => {
    if (!item.id) {
      return;
    }
    const oldAnswer = current[item.id] || { answerText: "", options: [], optionInputs: {} };
    const optionInputs = { ...(oldAnswer.optionInputs || {}) };
    (item.options || []).forEach(option => {
      if (option.id && optionInputs[option.id] === undefined) {
        optionInputs[option.id] = "";
      }
    });
    next[item.id] = {
      answerText: oldAnswer.answerText || "",
      options: oldAnswer.options || [],
      optionInputs
    };
  });
  return next;
}

export function hasGenderRule(rules: QuestionRule[]): boolean {
  return rules.some(rule => isExternalRule(rule) && normalizeText(rule.sourceField) === "gender");
}

export function hasAgeRule(rules: QuestionRule[]): boolean {
  return rules.some(rule => isExternalRule(rule) && normalizeText(rule.sourceField) === "age");
}

export function hasExternalRules(rules: QuestionRule[]): boolean {
  return hasGenderRule(rules) || hasAgeRule(rules);
}

export function getVisibleItems(
  items: QuestionItemForm[],
  rules: QuestionRule[],
  answers: AnswerMap,
  externalContext: ExternalContext
): QuestionItemForm[] {
  return items.filter(item => isItemVisible(item, rules, answers, externalContext));
}

export function isItemVisible(
  item: QuestionItemForm,
  rules: QuestionRule[],
  answers: AnswerMap,
  externalContext: ExternalContext
): boolean {
  let visible = item.isShow === 1;
  rules.forEach(rule => {
    if (rule.targetItemId !== item.id || !ruleMatched(rule, answers, externalContext)) {
      return;
    }
    if (rule.action === "show") {
      visible = true;
    }
    if (rule.action === "hide") {
      visible = false;
    }
  });
  return visible;
}

export function isItemRequired(
  item: QuestionItemForm,
  rules: QuestionRule[],
  answers: AnswerMap,
  externalContext: ExternalContext
): boolean {
  let required = item.isRequired === 1;
  rules.forEach(rule => {
    if (rule.targetItemId !== item.id || !ruleMatched(rule, answers, externalContext)) {
      return;
    }
    if (rule.action === "required") {
      required = true;
    }
    if (rule.action === "not_required") {
      required = false;
    }
  });
  return required;
}

export function validateSubmitAnswers(
  items: QuestionItemForm[],
  rules: QuestionRule[],
  answers: AnswerMap,
  externalContext: ExternalContext
): string {
  if (hasGenderRule(rules) && !externalContext.checkerGender) {
    return "请选择模拟性别";
  }
  if (hasAgeRule(rules) && (externalContext.checkerAge === null || externalContext.checkerAge === undefined)) {
    return "请输入模拟年龄";
  }
  const visibleItems = getVisibleItems(items, rules, answers, externalContext);
  for (const item of visibleItems) {
    if (!isItemRequired(item, rules, answers, externalContext)) {
      continue;
    }
    const itemAnswer = answers[item.id as number] || emptyAnswer();
    if (item.itemType === "text" && !String(itemAnswer.answerText || "").trim()) {
      return `请填写：${item.itemTitle}`;
    }
    if (item.itemType !== "text" && itemAnswer.options.length === 0) {
      return `请选择：${item.itemTitle}`;
    }
  }
  return "";
}

export function buildSubmitItems(items: QuestionItemForm[], answers: AnswerMap): QuestionSubmitItem[] {
  return items.map(item => ({
    itemId: item.id as number,
    answerText: getAnswer(answers, item).answerText,
    options: buildSubmitOptions(getAnswer(answers, item))
  }));
}

export function buildSubmitOptions(itemAnswer: ItemAnswer): OptionAnswer[] {
  return itemAnswer.options.map(row => ({
    optionId: row.optionId,
    optionInput: itemAnswer.optionInputs[row.optionId] || ""
  }));
}

export function optionSelected(itemAnswer: ItemAnswer, option: QuestionOptionForm): boolean {
  return itemAnswer.options.some(row => row.optionId === option.id);
}

export function optionDisabled(
  item: QuestionItemForm,
  option: QuestionOptionForm,
  itemAnswer: ItemAnswer
): boolean {
  if (item.itemType !== "multiple") {
    return false;
  }
  const selectedExclusive = (item.options || []).some(row =>
    row.isExclusive === 1 && row.id !== option.id && optionSelected(itemAnswer, row)
  );
  return selectedExclusive && option.isExclusive !== 1;
}

export function toggleOption(item: QuestionItemForm, option: QuestionOptionForm, itemAnswer: ItemAnswer): void {
  if (optionDisabled(item, option, itemAnswer)) {
    return;
  }
  if (item.itemType === "single") {
    itemAnswer.options = [{ optionId: option.id as number }];
    return;
  }
  if (optionSelected(itemAnswer, option)) {
    itemAnswer.options = itemAnswer.options.filter(row => row.optionId !== option.id);
    return;
  }
  if (option.isExclusive === 1) {
    itemAnswer.options = [{ optionId: option.id as number }];
    return;
  }
  itemAnswer.options = itemAnswer.options.filter(row => {
    const source = (item.options || []).find(optionItem => optionItem.id === row.optionId);
    return source && source.isExclusive !== 1;
  });
  itemAnswer.options.push({ optionId: option.id as number });
}

export function getAnswer(answers: AnswerMap, item: QuestionItemForm): ItemAnswer {
  return answers[item.id as number] || emptyAnswer();
}

function ruleMatched(rule: QuestionRule, answers: AnswerMap, externalContext: ExternalContext): boolean {
  if (isExternalRule(rule)) {
    return externalRuleMatched(rule, externalContext);
  }
  return insideRuleMatched(rule, answers[rule.itemId as number] || emptyAnswer());
}

function insideRuleMatched(rule: QuestionRule, answer: ItemAnswer): boolean {
  const conditions = normalizeText(rule.conditions);
  const selectedIds = answer.options.map(row => row.optionId);
  const answerText = answer.answerText || "";
  const hasAnswer = selectedIds.length > 0 || !!answerText;
  if (conditions === "empty") {
    return selectedIds.length === 0 && !answerText;
  }
  if (conditions === "not_empty") {
    return selectedIds.length > 0 || !!answerText;
  }
  if (!hasAnswer) {
    return false;
  }
  if (rule.optionId) {
    const selected = selectedIds.indexOf(rule.optionId) > -1;
    return conditions === "ne" || conditions === "not_contains" ? !selected : selected;
  }
  const compare = rule.compareValue || "";
  if (conditions === "eq") { return answerText === compare; }
  if (conditions === "ne") { return answerText !== compare; }
  if (conditions === "contains") { return answerText.indexOf(compare) > -1; }
  if (conditions === "not_contains") { return answerText.indexOf(compare) === -1; }
  return false;
}

function externalRuleMatched(rule: QuestionRule, externalContext: ExternalContext): boolean {
  const conditions = normalizeText(rule.conditions);
  const sourceField = normalizeText(rule.sourceField);
  if (sourceField === "gender") {
    const gender = normalizeGender(externalContext.checkerGender);
    if (!gender) {
      return false;
    }
    const matched = String(rule.compareValue || "")
      .split(/[,，、]/)
      .map(normalizeGender)
      .some(item => item === gender);
    return conditions === "ne" || conditions === "not_in" ? !matched : matched;
  }
  if (sourceField === "age") {
    if (externalContext.checkerAge === null || externalContext.checkerAge === undefined) {
      return false;
    }
    return compareAge(externalContext.checkerAge, conditions, rule.compareValue || "");
  }
  return false;
}

function compareAge(age: number, conditions: string, compareValue: string): boolean {
  if (!compareValue) {
    return false;
  }
  if (conditions === "between") {
    const range = compareValue.split(/[-~～,，]/);
    return range.length === 2 && age >= Number(range[0]) && age <= Number(range[1]);
  }
  const value = Number(compareValue);
  if (conditions === "eq") { return age === value; }
  if (conditions === "ne") { return age !== value; }
  if (conditions === "gt") { return age > value; }
  if (conditions === "gte") { return age >= value; }
  if (conditions === "lt") { return age < value; }
  if (conditions === "lte") { return age <= value; }
  return false;
}

function isExternalRule(rule: QuestionRule): boolean {
  const sourceType = normalizeText(rule.sourceType);
  const sourceField = normalizeText(rule.sourceField);
  return sourceType === "external" || sourceField === "gender" || sourceField === "age";
}

function normalizeGender(value?: string): string {
  const text = normalizeText(value);
  if (text === "1" || text === "男" || text === "男性" || text === "male" || text === "m") {
    return "male";
  }
  if (text === "2" || text === "女" || text === "女性" || text === "female" || text === "f") {
    return "female";
  }
  return text;
}

function normalizeText(value?: string): string {
  return value ? value.trim().toLowerCase() : "";
}

function emptyAnswer(): ItemAnswer {
  return { answerText: "", options: [], optionInputs: {} };
}
